/**
 * Tasks documentation page content.
 *
 * Creating reusable task definitions, attaching to teammates,
 * running manually, and connecting triggers.
 */

export const tasksContent = `
A **task** is a reusable instruction template attached to a teammate. Define the work once, then trigger it manually, on a schedule, via webhook, or by email.

## Create a Task

\`\`\`python
from m8tes import M8tes

client = M8tes()

task = client.tasks.create(
    teammate_id=1,
    instructions="Check inbox for new support emails and draft replies",
    name="Email Triage",
    expected_output="Summary of emails processed and replies drafted",
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/tasks \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "teammate_id": 1,
    "instructions": "Check inbox for new support emails and draft replies",
    "name": "Email Triage",
    "expected_output": "Summary of emails processed and replies drafted"
  }'
\`\`\`

### Fields

| Field | Type | Description |
|-------|------|-------------|
| \`teammate_id\` | int (required) | The teammate that runs this task |
| \`instructions\` | string (required) | What the teammate should do |
| \`name\` | string | Display name for the task |
| \`tools\` | string[] | Override teammate tools for this task |
| \`expected_output\` | string | Description of expected output format |
| \`goals\` | string | Task-specific goals |
| \`user_id\` | string | End-user ID for [multi-tenancy](/docs/users) |

## Run a Task

Execute a saved task on demand:

\`\`\`python
# Streaming (default) — iterate over events
for event in client.tasks.run(task_id=task.id):
    print(event.type, event.raw)

# Non-streaming — returns immediately, poll for result
run = client.tasks.run(task_id=task.id, stream=False)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

\`\`\`bash
# Streaming
curl -X POST https://m8tes.ai/api/v2/tasks/1/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{}'

# Non-streaming
curl -X POST https://m8tes.ai/api/v2/tasks/1/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"stream": false}'
\`\`\`

For automated execution, attach a trigger instead — see below.

## Run Tasks with Triggers

Tasks become automated when you attach a trigger:

| Trigger | How it works |
|---------|-------------|
| **[Scheduling](/docs/scheduling)** | Cron expressions or fixed intervals |
| **[Webhook](/docs/webhook-triggers)** | External systems POST to a URL to trigger the task |
| **[Email Inbox](/docs/email-inbox)** | Inbound emails trigger a run |

### Quick Example: Schedule a Task

\`\`\`python
trigger = client.tasks.triggers.create(
    task_id=task.id,
    type="schedule",
    cron="0 9 * * 1",  # Every Monday at 9am UTC
)
\`\`\`

### Quick Example: Webhook Trigger

\`\`\`python
trigger = client.tasks.triggers.create(
    task_id=task.id,
    type="webhook",
)
print(trigger.url)  # POST to this URL to trigger the task
\`\`\`

### Quick Example: Email Trigger

\`\`\`python
trigger = client.tasks.triggers.create(
    task_id=task.id,
    type="email",
)
print(trigger.address)  # Send emails here to trigger the task
\`\`\`

## List Tasks

\`\`\`python
page = client.tasks.list()
for task in page.data:
    print(task.id, task.name, task.instructions)

# Filter by teammate
page = client.tasks.list(teammate_id=1)
\`\`\`

\`\`\`bash
curl "https://m8tes.ai/api/v2/tasks?teammate_id=1" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Update a Task

\`\`\`python
task = client.tasks.update(
    task_id=1,
    instructions="Updated: check inbox and categorize by priority",
)
\`\`\`

\`\`\`bash
curl -X PATCH https://m8tes.ai/api/v2/tasks/1 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"instructions": "Updated: check inbox and categorize by priority"}'
\`\`\`

## Delete a Task

\`\`\`python
client.tasks.delete(task_id=1)
\`\`\`

\`\`\`bash
curl -X DELETE https://m8tes.ai/api/v2/tasks/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## What's Next

- [Scheduling](/docs/scheduling) — automate tasks with cron and intervals
- [Webhook Triggers](/docs/webhook-triggers) — trigger tasks from external systems
- [Runs](/docs/runs) — understand run execution and streaming
`;
